import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { QuillEditorComponent } from 'ngx-quill'
import Quill from 'quill'
import { firstValueFrom } from 'rxjs'
import { generateGuid } from 'src/app/shared/shared-functions'

import { ListItem, Notification, RepositoryModal } from '../../shared'

@Component({
    templateUrl: './notification.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, QuillEditorComponent, FaIconComponent],
})
export class NotificationComponent extends RepositoryModal<Notification> implements OnInit, AfterViewInit {
    @ViewChild('quillEditorRef', { static: true }) quillEditorRef: any
    private changeDetector = inject(ChangeDetectorRef)

    get quillEditor(): Quill {
        return this.quillEditorRef.quillEditor
    }

    editorModules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['link'],
                ['attachment'], // 👈 matches the custom handler name
            ],
            handlers: {
                attachment: () => this.handleAttachmentUpload(),
            },
        },
        clipboard: {
            matchVisual: false,
        },
        handlers: {
            attachment: this.handleAttachmentUpload.bind(this), // 👈 custom handler
        },
    }

    recipientsMap: ListItem<string>[] = []

    ngOnInit() {
        if (this.isAdding) {
            this.target.groupIds = []
            this.target.id = generateGuid()
        }

        if (!this.target.expiresOn) {
            const oneWeekOut = new Date()
            oneWeekOut.setDate(oneWeekOut.getDate() + 7)
            this.target.expiresOn = oneWeekOut
        }

        const map = this.services.data.groups.items.map(group => new ListItem(group.id, group.name))
        map.forEach(item => (item.$checked = this.target.groupIds.includes(item.value)))
        this.recipientsMap = map

        const icons = Quill.import('ui/icons') as any
        icons['attachment'] = '📎'
    }

    ngAfterViewInit() {
        setTimeout(() => {
            const button = document.querySelector('button.ql-attachment') as HTMLButtonElement
            if (button) {
                button.setAttribute('title', 'Attach a file')
            }
            const link = document.querySelector('button.ql-link') as HTMLButtonElement
            if (link) {
                link.setAttribute('title', 'Add link to selected text')
            }
        }, 500)
    }

    protected onBeforeSave(): boolean {
        this.target.groupIds = this.recipientsMap.filter(item => item.$checked).map(item => item.value)
        return true
    }

    handleAttachmentUpload() {
        const input = document.createElement('input')
        input.setAttribute('type', 'file')
        input.setAttribute('accept', '*') // or restrict to e.g. 'application/pdf,image/*'

        input.click()

        input.onchange = async () => {
            if (input.files && input.files[0]) {
                const file = input.files[0]

                // Upload the file to your backend or storage service
                const result = await this.uploadFile(file)
                if (!result) {
                    this.services.notification.error('Failed to upload file')
                    return
                }

                const uploadedUrl = `https://webapps.wedigutah.com/Resources/Images/Notification/${this.target.id}/${file.name}`

                // Insert a link or preview into the editor
                const range = this.quillEditor.getSelection(true)

                const fileName = file.name

                // Insert as a link
                this.quillEditor.insertText(range.index, fileName, {
                    link: uploadedUrl,
                })

                // Manually trigger the model update
                const editorContent = this.quillEditor.root.innerHTML
                this.target.richBody = editorContent // Replace 'content' with your actual bound property
                this.changeDetector.detectChanges() // Optional: ensure view updates

                // Optional: move cursor after inserted content
                setTimeout(() => {
                    this.quillEditor.setSelection(range.index + fileName.length)
                }, 10)
            }
        }
    }

    uploadFile(file: File): Promise<string> {
        const uploadData = new FormData()
        uploadData.append('id', this.target.id)
        uploadData.append('myFile', file, file.name)
        return firstValueFrom(this.services.data.post('api/Notification/UploadAttachment', uploadData))
    }
}
